import { useMutation } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { api, ApiError, fieldIssues } from '../api';
import { can, fieldVisible, type Me } from '../auth/me';
import { ClientField, DateField } from '../events/EventFields';
import { lastDayAfterFirstChange } from '../events/fields';
import { EVENTS_RESOURCE } from '../events/labels';
import { QUOTES_RESOURCE, type QuoteOfEvent } from './types';
import { DialogLayer } from '../ui/Dialog';
import { useUnsavedGuard } from '../ui/unsaved';

/** "Nowa wycena" makes an event too: both rights are needed (the button, the dialog and the server agree). */
export const canStartQuote = (me: Me): boolean => can(me, QUOTES_RESOURCE, 'create') && can(me, EVENTS_RESOURCE, 'create');

type Picked = { id: string; name: string };

/**
 * "Nowa wycena" (P16, point 6): name, client, days and (optionally) the place of a new event; the server makes the
 * event (status "Wycena", flag "potrzebna wycena") and its quote at once, then the quote editor opens.
 * The date is required: the quote takes its number of days from it (decision P16).
 */
export function NewQuoteDialog({ me, onCreated, onClose }: { me: Me; onCreated: (eventId: string) => void; onClose: () => void }) {
  const [name, setName] = useState('');
  const [client, setClient] = useState<Picked | null>(null);
  const [firstDay, setFirstDay] = useState('');
  const [lastDay, setLastDay] = useState('');
  const [location, setLocation] = useState('');
  const [local, setLocal] = useState('');
  const showClient = fieldVisible(me, EVENTS_RESOURCE, 'contractorId');
  const showPlace = fieldVisible(me, EVENTS_RESOURCE, 'location');
  const create = useMutation({
    mutationFn: () =>
      api<QuoteOfEvent>('/api/quotes/new', {
        method: 'POST',
        body: { name, firstDay, lastDay, ...(client ? { contractorId: client.id } : {}), ...(location.trim() !== '' ? { location } : {}) },
      }),
    onSuccess: (q) => q.quote && onCreated(q.quote.eventId),
  });
  function ready(): boolean {
    if (name.trim() === '') return (setLocal('Wpisz nazwę wydarzenia'), false);
    if (!firstDay || !lastDay) return (setLocal('Wybierz pierwszy i ostatni dzień'), false);
    setLocal('');
    return true;
  }
  function submit(e: FormEvent) {
    e.preventDefault();
    if (ready()) create.mutate();
  }
  // owner 2026-10-23 21:14: Escape or leaving with unsaved changes asks „Zapisz / Nie zapisuj / Anuluj”
  const guardRef = useUnsavedGuard({
    dirty: name.trim() !== '' || client !== null || firstDay !== '' || lastDay !== '' || location.trim() !== '',
    save: async () => {
      if (!ready()) return false;
      await create.mutateAsync();
      return true;
    },
  });
  const error = create.error instanceof ApiError ? create.error : null;
  return (
    <div className="dialog-backdrop">
      <form ref={guardRef} className="dialog panel" role="dialog" aria-modal="true" aria-labelledby="new-quote-title" onSubmit={submit} data-feature-id="quotes.new_with_event">
        <DialogLayer onClose={onClose} busy={create.isPending} />
        <h2 id="new-quote-title">Nowa wycena</h2>
        <p className="muted">Powstanie wydarzenie ze statusem „Wycena” i jego kosztorys. Resztę danych wydarzenia uzupełnisz później.</p>
        <label className="field">
          <span>Nazwa wydarzenia</span>
          <input value={name} onChange={(e) => setName(e.target.value)} autoComplete="off" autoFocus placeholder="np. Wesele Kowalskich" />
        </label>
        {showClient && <ClientField picked={client} onPick={setClient} canCreate={can(me, 'contractors.contractors', 'create')} />}
        <div className="field-row">
          <DateField
            label="Pierwszy dzień"
            required
            value={firstDay}
            onChange={(day) => {
              setLastDay(lastDayAfterFirstChange({ oldFirst: firstDay, newFirst: day, last: lastDay }));
              setFirstDay(day);
            }}
          />
          <DateField label="Ostatni dzień" required value={lastDay} {...(firstDay ? { min: firstDay } : {})} onChange={setLastDay} />
        </div>
        {showPlace && (
          <label className="field">
            <span>Miejsce (nieobowiązkowo)</span>
            <input value={location} onChange={(e) => setLocation(e.target.value)} autoComplete="off" placeholder="np. Hotel Haffner, Sopot" />
          </label>
        )}
        {(local || error) && (
          <p className="alert" role="alert">
            {local || (error && (fieldIssues(error)[0]?.message ?? error.message))}
          </p>
        )}
        <div className="actions">
          <button className="btn primary" type="submit" disabled={create.isPending}>
            {create.isPending ? 'Tworzenie…' : 'Utwórz i otwórz kosztorys'}
          </button>
          <button className="btn" type="button" onClick={onClose}>
            Anuluj
          </button>
        </div>
      </form>
    </div>
  );
}
